import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Modal,
  IconButton,
  CardContent,
  Button,
  Divider,
  Skeleton,
  useTheme,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import { useDispatch, useSelector } from "react-redux";
import { fetchCampaigns } from "../../reducers/campaignsReducer";
import DOMPurify from 'dompurify';
import { Share } from '@capacitor/share';
import InfoModal from '../../components/InfoModal';

const BannerContent = ({ content }) => {
  const sanitizedContent = DOMPurify.sanitize(content);
  
  return (
    <Box
      component="div"
      className="banner-content"
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      sx={{
        color: '#fff',
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          color: '#fff',
          margin: '0 0 0.5rem 0',
          fontWeight: 'bold',
          fontSize: {
            xs: '1.25rem',
            sm: '1.5rem'
          },
          lineHeight: 1.2,
        },
        '& p': {
          color: 'rgba(255, 255, 255, 0.9)',
          margin: '0 0 0.5rem 0',
          fontSize: {
            xs: '0.875rem',
            sm: '1rem'
          },
          lineHeight: 1.5,
        },
        '& a': {
          color: '#fff',
          textDecoration: 'underline',
          '&:hover': {
            opacity: 0.8,
          },
        },
        '& img': {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 1,
        },
        '& ul, & ol': {
          paddingLeft: 2,
          marginBottom: 1,
          color: 'rgba(255, 255, 255, 0.9)',
        },
        '& li': {
          marginBottom: 0.5,
          color: 'rgba(255, 255, 255, 0.9)',
        },
      }}
    />
  );
};

const handleShare = async (referralCode) => {
  try {
    await Share.share({
      title: 'Join me on Crobo!',
      text: `Use my referral code: ${referralCode} and get rewarded!`,
      url: `https://www.crobo.money/signup/?referral_code=${referralCode}&utm_medium=link&utm_campaign=referral`,
      dialogTitle: 'Share with friends',
    });
  } catch (error) {
    console.error('Error sharing:', error);
    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(
        `Join me on Crobo!\nUse my referral code: ${referralCode} and get rewarded!\n${window.location.origin}`
      );
      console.log("Copied to clipboard!");
    } catch (err) {
      console.error('Clipboard fallback failed:', err);
    }
  }
};

export const Banner = ({ navigate, profile }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openTnC, setOpenTnC] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const { campaigns, loading: loadingCampaigns } = useSelector(
    (state) => state.campaigns
  );
  const envStore = useSelector((state) => {
    return state.resource?.envList;
  });

  const handleOpenTnC = (campaign) => {
    setSelectedCampaign(campaign);
    setOpenTnC(true);
  };

  const handleCloseTnC = () => {
    setOpenTnC(false);
    setSelectedCampaign(null);
  };

  useEffect(() => {
    dispatch(fetchCampaigns());
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        gap: 2,
        overflowX: "auto",
        pb: 2,
        "&::-webkit-scrollbar": {
          display: "none",
        },
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {/* Promo Card */}
      {!loadingCampaigns && (
        campaigns.map((campaign) => (
          
          <React.Fragment key={campaign.id}>
            <Card
              sx={{
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? theme.palette.brand.gradient 
                    : theme.palette.brand.gradient,
                color: "text.invert",
                position: "relative",
                overflow: "hidden",
                minWidth: { xs: "90%", md: "90%" },
                flex: { xs: "0 0 90%", md: "0 0 90%" },
                minHeight: "100%",
                boxShadow: (theme) => {
                  const brandColor = theme.palette.mode === "dark" 
                    ? theme.palette.brand.main 
                    : theme.palette.brand.main;
                  return `0 8px 16px ${brandColor}40`; // 40 is hex opacity
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  right: "-10%",
                  top: "-20%",
                  width: "180px",
                  height: "180px",
                  background: `url('data:image/svg+xml,${encodeURIComponent(
                    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="rgba(255,255,255,0.1)" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-4-4h3V9h2v4h3l-4 4z"/></svg>'
                  )}')`,
                  backgroundRepeat: "no-repeat",
                  transform: "rotate(-30deg)",
                  opacity: 0.1,
                  pointerEvents: "none",
                  zIndex: 0,
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: "5%",
                  bottom: "-15%",
                  width: "120px",
                  height: "120px",
                  background: `url('data:image/svg+xml,${encodeURIComponent(
                    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="rgba(255,255,255,0.1)" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>'
                  )}')`,
                  backgroundRepeat: "no-repeat",
                  transform: "rotate(25deg)",
                  opacity: 0.1,
                  pointerEvents: "none",
                  zIndex: 0,
                }
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  right: "10%",
                  top: "10%",
                  width: "50px",
                  height: "50px",
                  border: "2px solid rgba(255,255,255,0.1)",
                  borderRadius: "10px",
                  transform: "rotate(35deg)",
                  pointerEvents: "none",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: "8%",
                  bottom: "15%",
                  width: "35px",
                  height: "35px",
                  border: "2px solid rgba(255,255,255,0.1)",
                  borderRadius: "8px",
                  transform: "rotate(-20deg)",
                  pointerEvents: "none",
                }}
              />
              <CardContent sx={{ 
                display: "flex", 
                flexDirection: "column", 
                justifyContent: "space-between",
                height: "100%",
                position: "relative",
                zIndex: 1
              }}>
                <Box>
                  <Grid item xs={12} sm={7}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{
                        mb: 1.5,
                        color: "#fff",
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      }}
                    >
                      {campaign.banner && (
                        <BannerContent
                          content={campaign.banner.bannerContent}
                        />
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.9,
                        color: "rgba(255, 255, 255, 0.9)",
                        fontSize: {
                          xs: "0.75rem",
                          sm: "0.875rem",
                        },
                        lineHeight: 1.5,
                        mb: 2,
                      }}
                    >
                      {campaign.description}
                    </Typography>
                  </Grid>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: "auto",
                    pt: 2
                  }}
                >
                  {campaign?.banner?.termsAndConditions && (
                    <Button
                      onClick={() => handleOpenTnC(campaign)}
                      target="_blank"
                      size="small"
                      sx={{
                        color: (theme) => theme.palette.custom.mint,
                        fontSize: "0.75rem",
                        textDecoration: "underline",
                        "&:hover": {
                          bgcolor: "rgba(255,215,0,0.1)",
                        },
                        py: 0.5,
                      }}
                    >
                      View T&C
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>

            <InfoModal 
              open={openTnC}
              onClose={handleCloseTnC}
              title={`Terms & Conditions - ${campaign?.campaignCode || ''}`}
              content={selectedCampaign?.banner?.termsAndConditions || ''}
              sanitizeContent={true}
              modalProps={{
                sx: {
                  zIndex: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }
              }}
              contentProps={{
                sx: {
                  maxHeight: '60vh',
                  overflowY: 'auto',
                }
              }}
            />
          </React.Fragment>
        ))
      )}

      {/* Referral Card */}
      <Card
        onClick={() => {
          if (profile?.referral_code) {
            handleShare(profile.referral_code);
          }
        }}
        sx={{
          background: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.custom.gradients.secondaryDark ||
                "linear-gradient(45deg, #2c1810 30%, #3d2317 90%)"
              : theme.palette.custom.gradients.secondary,
          color: "text.invert",
          position: "relative",
          overflow: "hidden",
          minWidth: { xs: "100%", md: "100%" },
          flex: { xs: "0 0 100%", md: "0 0 100%" },
          cursor: "pointer",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
          boxShadow: (theme) => {
            const secondaryColor = theme.palette.mode === "dark"
              ? theme.palette.custom.secondaryDark || '#3d2317'
              : theme.palette.custom.secondary || '#2c1810';
            return `0 8px 16px ${secondaryColor}40`; // 40 is hex opacity
          },
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: (theme) => {
              const secondaryColor = theme.palette.mode === "dark"
                ? theme.palette.custom.secondaryDark || '#3d2317'
                : theme.palette.custom.secondary || '#2c1810';
              return `0 12px 24px ${secondaryColor}60`; // 60 is hex opacity
            },
          },
          "&::before": {
            content: '""',
            position: "absolute",
            right: "-20%",
            top: "-30%",
            width: "200px",
            height: "200px",
            background: `url('data:image/svg+xml,${encodeURIComponent(
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="rgba(255,255,255,0.1)" d="M22 4h-2c-.55 0-1 .45-1 1v9c0 .55.45 1 1 1h2V4zM2.17 11.12c-.11.25-.17.52-.17.8V13c0 1.1.9 2 2 2h5.5l-.92 4.65c-.05.22-.02.46.08.66.23.45.52.86.88 1.22L10 22l6.41-6.41c.38-.38.59-.89.59-1.42V6.34C17 5.05 15.95 4 14.66 4h-8.1c-.71 0-1.36.37-1.72.97l-2.67 6.15z"/></svg>'
            )}')`,
            backgroundRepeat: "no-repeat",
            transform: "rotate(-45deg)",
            opacity: 0.1,
            pointerEvents: "none",
            zIndex: 0,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            left: "10%",
            bottom: "-20%",
            width: "150px",
            height: "150px",
            background: `url('data:image/svg+xml,${encodeURIComponent(
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="rgba(255,255,255,0.1)" d="M15 4c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zM3 12c0-2.61 1.67-4.83 4-5.65V4.26C3.55 5.15 1 8.27 1 12s2.55 6.85 6 7.74v-2.09c-2.33-.82-4-3.04-4-5.65z"/></svg>'
            )}')`,
            backgroundRepeat: "no-repeat",
            transform: "rotate(30deg)",
            opacity: 0.1,
            pointerEvents: "none",
            zIndex: 0,
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: "15%",
            top: "15%",
            width: "60px",
            height: "60px",
            border: "2px solid rgba(255,255,255,0.1)",
            borderRadius: "12px",
            transform: "rotate(45deg)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: "10%",
            bottom: "20%",
            width: "40px",
            height: "40px",
            border: "2px solid rgba(255,255,255,0.1)",
            borderRadius: "8px",
            transform: "rotate(-15deg)",
            pointerEvents: "none",
          }}
        />
        <CardContent
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            p: { xs: 1.5, sm: 2 },
          }}
        >
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  mb: 0.5,
                  color: "#fff",
                }}
              >
                Share & Get <span style={{ 
                  color: theme.palette.custom.mint,
                  fontWeight: 'bold',
                  fontSize: '1.5em'
                }}>${envStore?.REFERRER_AMOUNT || 25}</span> Reward!
              </Typography>
              <Typography
                variant="body1"
                sx={{ opacity: 0.9, mb: 2, color: "rgba(255, 255, 255, 0.9)" }}
              >
                Invite friends and earn rewards*
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  p: 1,
                  borderRadius: 2,
                }}
              >
                {profile?.referral_code ? (
                  <>
                    <Typography variant="body1" sx={{ flex: 1, color: "#fff" }}>
                      {profile.referral_code}
                    </Typography>
                    <Button
                      startIcon={<ShareIcon />}
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click when button is clicked
                        if (profile?.referral_code) {
                          handleShare(profile.referral_code);
                        }
                      }}
                      sx={{
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.9)"
                            : "text.invert",
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? "background.paper"
                            : "brand.main",
                        "&:hover": {
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.95)"
                              : "rgba(255, 255, 255, 0.9)",
                        },
                      }}
                    >
                      Share
                    </Button>
                  </>
                ) : (
                  <>
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={24}
                      sx={{ flex: 1, bgcolor: "rgba(255,255,255,0.1)" }}
                    />
                    <Skeleton
                      variant="rectangular"
                      width={100}
                      height={36}
                      sx={{ bgcolor: "rgba(255,255,255,0.1)" }}
                    />
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};
